import { type MutationFunctionContext, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { type Address } from 'viem';
import { useCofheWalletClient, useCofheChainId, useCofheAccount, useCofhePublicClient } from './useCofheConnection.js';
import { type ConfidentialToken } from './useCofheTokenLists.js';
import { buildTokenUnshieldCallArgs } from '../constants/tokenTypeConfig.js';
import { assertTokenOperationSupported, getConfidentialDecimals, isTokenOperationSupported } from '@/types/token';
import { TransactionActionType, TransactionStatus, useTransactionStore } from '../stores/transactionStore.js';
import { useInternalMutation } from '../providers/index.js';
import { assert } from 'ts-essentials';
import { useOnceTransactionMined } from './useOnceTransactionMined.js';
import { useTransactionGlobalLifecycle } from './useTransactionGlobalLifecycle.js';
import type { CofheSimulateWriteContractCallArgs } from './useCofheSimulateWriteContract.js';

export function getCofheTokenUnshieldCallArgs(params: {
  token: ConfidentialToken;
  amount: bigint;
  account: Address;
}): CofheSimulateWriteContractCallArgs {
  const { token, amount: rawAmount, account } = params;
  const confidentialityType = token.extensions.fhenix.confidentialityType;

  if (!confidentialityType) {
    throw new Error('confidentialityType is required in token extensions');
  }

  assertTokenOperationSupported(confidentialityType, 'unshield');

  return buildTokenUnshieldCallArgs({
    token,
    amount: rawAmount,
    account,
  });
}

// ============================================================================
// Unshield Hook
// ============================================================================
type UseTokenUnshieldMutationInput = {
  /** Token object with confidentialityType */
  token: ConfidentialToken;
  /** Amount to unshield (in token's smallest unit) */
  amount: bigint;
  /** Optional callback for status updates during the operation */
  onStatusChange?: (message: string) => void;
};

type UseTokenUnshieldMutationOptions = Omit<
  UseMutationOptions<`0x${string}`, Error, UseTokenUnshieldMutationInput>,
  'mutationFn'
>;
/**
 * Hook to unshield tokens (initiate conversion from confidential to regular)
 * Wrapped tokens call `unshield(address from, address to, uint64 amount)` and then need to be claimed.
 * @param options - Optional React Query mutation options
 * @returns Mutation result with transaction hash
 */
function useCofheTokenUnshieldMutation(
  options?: UseTokenUnshieldMutationOptions
): UseMutationResult<`0x${string}`, Error, UseTokenUnshieldMutationInput> {
  const walletClient = useCofheWalletClient();
  const publicClient = useCofhePublicClient();
  const chainId = useCofheChainId();
  const account = useCofheAccount();
  const { onTransactionSubmitError } = useTransactionGlobalLifecycle();

  const { onSuccess, onError, ...restOfOptions } = options || {};

  return useInternalMutation({
    mutationFn: async (input: UseTokenUnshieldMutationInput) => {
      if (!walletClient) {
        throw new Error('WalletClient is required for token unshield');
      }

      if (!publicClient) {
        throw new Error('PublicClient is required to simulate unshield before writing');
      }

      const confidentialityType = input.token.extensions.fhenix.confidentialityType;

      if (!confidentialityType) {
        throw new Error('confidentialityType is required in token extensions');
      }

      if (!walletClient.account) {
        throw new Error('Wallet account is required for token unshield');
      }

      assertTokenOperationSupported(confidentialityType, 'unshield');

      let hash: `0x${string}`;
      const unshieldCallArgs = getCofheTokenUnshieldCallArgs({
        token: input.token,
        amount: input.amount,
        account: walletClient.account.address,
      });

      input.onStatusChange?.('Please confirm in wallet...');
      const { request } = await publicClient.simulateContract({
        ...unshieldCallArgs,
        account: walletClient.account,
      });
      hash = await walletClient.writeContract({ ...request, chain: undefined });

      return hash;
    },
    onError: (error, variables, onMutateResult, context) => {
      if (onError) onError(error, variables, onMutateResult, context);
      onTransactionSubmitError(error, TransactionActionType.Unshield);
    },
    onSuccess: async (hash, input, onMutateResult, context) => {
      assert(chainId, 'Chain ID is required for token unshield');
      assert(account, 'Wallet account is required for token unshield');
      if (onSuccess) await onSuccess(hash, input, onMutateResult, context);

      const requiresExternalDecryptionTracking =
        input.token.extensions.fhenix.confidentialityType !== 'dual' &&
        isTokenOperationSupported(input.token.extensions.fhenix.confidentialityType, 'claimable');

      useTransactionStore.getState().addTransaction({
        hash,
        token: input.token,
        tokenAmount: input.amount,
        tokenAmountDecimals: getConfidentialDecimals(input.token),
        chainId,
        actionType: TransactionActionType.Unshield,
        account,
      });
    },
    ...restOfOptions,
  });
}

type UseCofheTokenUnshieldInput = {
  onUserSignatureRequest?: (variables: UseTokenUnshieldMutationInput, context: MutationFunctionContext) => void;
  onTransactionSubmitSuccess?: (hash: `0x${string}`) => void;
  onTransactionSubmitError?: (error: Error) => void;
  onceMined?: (transaction: { hash: `0x${string}`; status: TransactionStatus }) => void;
};
export function useCofheTokenUnshield(input: UseCofheTokenUnshieldInput) {
  const unshieldMutation = useCofheTokenUnshieldMutation({
    onMutate: input.onUserSignatureRequest,
    onSuccess: input.onTransactionSubmitSuccess,
    onError: input.onTransactionSubmitError,
  });

  const { isMining: isTokenUnshieldMining } = useOnceTransactionMined({
    txHash: unshieldMutation.data,
    onceMined: input.onceMined,
  });

  return {
    ...unshieldMutation,
    isTokenUnshieldMining,
  };
}
